import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuthContext } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { subDays, format } from 'date-fns';

export type EventType = 'termin' | 'meilenstein' | 'deadline';

export interface CalendarEvent {
  id: string;
  title: string;
  description: string | null;
  event_type: EventType;
  start_date: string;
  end_date: string | null;
  all_day: boolean;
  project_id: string | null;
  assigned_to: string | null;
  created_by: string | null;
  reminder_minutes: number | null;
  color: string | null;
  created_at: string;
  updated_at: string;
}

export interface EventInsert {
  title: string;
  description?: string | null;
  event_type?: EventType;
  start_date: string;
  end_date?: string | null;
  all_day?: boolean;
  project_id?: string | null;
  assigned_to?: string | null;
  reminder_minutes?: number | null;
  color?: string | null;
}

export interface EventInsertWithExtras extends EventInsert {
  assignees?: string[];
  createPrepTask?: boolean;
}

export const fetchCalendarEventsFromDb = async (startDate?: Date, endDate?: Date): Promise<CalendarEvent[]> => {
  let query = supabase
    .from('calendar_events')
    .select('*')
    .order('start_date', { ascending: true });

  if (startDate) {
    query = query.gte('start_date', startDate.toISOString());
  }
  if (endDate) {
    query = query.lte('start_date', endDate.toISOString());
  }

  const { data, error } = await query;

  if (error) throw error;
  return data as CalendarEvent[];
};

export const useCalendarEvents = (startDate?: Date, endDate?: Date) => {
  const { user, initialized } = useAuthContext();
  const queryClient = useQueryClient();

  const { data: events, isLoading, error } = useQuery({
    queryKey: ['calendar-events', startDate?.toISOString(), endDate?.toISOString()],
    queryFn: () => fetchCalendarEventsFromDb(startDate, endDate),
    enabled: !!user && initialized,
  });

  // Get assignees for an event
  const getEventAssignees = async (eventId: string): Promise<string[]> => {
    const { data, error } = await supabase
      .from('calendar_event_assignees')
      .select('user_id')
      .eq('event_id', eventId);

    if (error) {
      console.error('Error fetching event assignees:', error);
      return [];
    }

    return data.map(a => a.user_id);
  };

  // Update assignees for an event
  const updateEventAssignees = async (eventId: string, userIds: string[]) => {
    // First, delete all existing assignees
    await supabase
      .from('calendar_event_assignees')
      .delete()
      .eq('event_id', eventId);

    // Then insert new assignees
    if (userIds.length > 0) {
      const { error } = await supabase
        .from('calendar_event_assignees')
        .insert(userIds.map(userId => ({
          event_id: eventId,
          user_id: userId,
        })));

      if (error) {
        console.error('Error updating event assignees:', error);
        throw error;
      }
    }

    queryClient.invalidateQueries({ queryKey: ['calendar-events'] });
  };

  // Create preparation task for an event
  const createPreparationTask = async (
    eventTitle: string,
    eventDate: string,
    projectId: string | null,
    assigneeIds: string[]
  ) => {
    // Calculate due date: 1 day before the event
    const eventDateObj = new Date(eventDate);
    const dueDate = subDays(eventDateObj, 1);

    // We need a project_id for tasks - use the first project if none specified
    let taskProjectId = projectId;
    if (!taskProjectId) {
      const { data: projects } = await supabase
        .from('projects')
        .select('id')
        .limit(1)
        .single();
      
      if (projects) {
        taskProjectId = projects.id;
      }
    }

    if (!taskProjectId) {
      console.error('No project found for preparation task');
      return;
    }

    // Create the task
    const { data: task, error: taskError } = await supabase
      .from('tasks')
      .insert({
        title: `Vorbereitung: ${eventTitle}`,
        description: `Vorbereitung für den Termin "${eventTitle}" am ${format(eventDateObj, 'dd.MM.yyyy')}`,
        project_id: taskProjectId,
        due_date: dueDate.toISOString(),
        priority: 'mittel',
        status: 'offen',
        category: 'Terminvorbereitung',
        created_by: user?.id,
      })
      .select()
      .single();

    if (taskError) {
      console.error('Error creating preparation task:', taskError);
      return;
    }

    // Assign the task to the same people as the event
    if (task && assigneeIds.length > 0) {
      await supabase
        .from('task_assignees')
        .insert(assigneeIds.map(userId => ({
          task_id: task.id,
          user_id: userId,
        })));
    }

    queryClient.invalidateQueries({ queryKey: ['tasks'] });
    toast.success('Vorbereitungsaufgabe erstellt');
  };

  const createEvent = useMutation({
    mutationFn: async (eventWithExtras: EventInsertWithExtras) => {
      const { assignees, createPrepTask, ...event } = eventWithExtras;

      // Create the event
      const { data, error } = await supabase
        .from('calendar_events')
        .insert({
          ...event,
          created_by: user?.id,
        })
        .select()
        .single();

      if (error) throw error;

      // Add assignees
      if (assignees && assignees.length > 0) {
        await supabase
          .from('calendar_event_assignees')
          .insert(assignees.map(userId => ({
            event_id: data.id,
            user_id: userId,
          })));
      }

      // Create preparation task if requested
      if (createPrepTask && assignees && assignees.length > 0) {
        await createPreparationTask(
          event.title,
          event.start_date,
          event.project_id || null,
          assignees
        );
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendar-events'] });
      toast.success('Termin erstellt');
    },
    onError: (error) => {
      toast.error('Fehler beim Erstellen: ' + error.message);
    },
  });

  const updateEvent = useMutation({
    mutationFn: async ({ id, ...updates }: Partial<CalendarEvent> & { id: string }) => {
      const { data, error } = await supabase
        .from('calendar_events')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendar-events'] });
      toast.success('Termin aktualisiert');
    },
    onError: (error) => {
      toast.error('Fehler beim Aktualisieren: ' + error.message);
    },
  });

  const deleteEvent = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('calendar_events')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendar-events'] });
      toast.success('Termin gelöscht');
    },
    onError: (error) => {
      toast.error('Fehler beim Löschen: ' + error.message);
    },
  });

  return {
    events: events ?? [],
    isLoading,
    error,
    createEvent,
    updateEvent,
    deleteEvent,
    getEventAssignees,
    updateEventAssignees,
  };
};
